import React, { useEffect, useState } from 'react';
import { Box, FormControl } from '@mui/material';
import { useAuth } from '../../security/AuthProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {AuthErrorCode, getErrorMessage} from "../../api/types";
import { Button } from '../../components/Button';
import { TextField } from '../../components/TextField';
import { AuthFormContainer } from '../../components/AuthFormContainer';
import { ErrorAlert } from '../../components/ErrorAlert';
import Spinner from '../../ui/Spinner';

interface ResetPasswordProps {}

const ResetPassword: React.FC<ResetPasswordProps> = () => {

    const { validatePasswordResetToken, changePassword } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [resetToken, setResetToken] = useState<string>();
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmedPassword, setConfirmedPassword] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
    const [tokenValid, setTokenValid] = useState(false);

    useEffect(() => {
        const tokenFromUrl = searchParams.get('token');
        if (tokenFromUrl) {
            setResetToken(tokenFromUrl);
        }
    }, [searchParams]);

    useEffect(() => {
        if (resetToken) {
            validateToken(resetToken);
        }
    }, [resetToken]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!resetToken || !newPassword || !confirmedPassword) return;

        setLoading(true);
        setError(null);
        setErrorCode(null);

        try {
            const result = await changePassword(newPassword, confirmedPassword, resetToken);

            if (result.success) {
                navigate('/login', {
                    replace: true,
                    state: { message: 'Passwort erfolgreich geändert. Bitte melden Sie sich an.' }
                });
            } else {
                const code = result.errorCode || AuthErrorCode.UNEXPECTED_ERROR;
                setErrorCode(code);
                setError(getErrorMessage(code));
            }
        } catch (err) {
            console.error('Change password error:', err);
            setErrorCode(AuthErrorCode.UNEXPECTED_ERROR);
            setError(getErrorMessage(AuthErrorCode.UNEXPECTED_ERROR));
        } finally {
            setLoading(false);
        }
    };

    const validateToken = async (token: string) => {
        if (!token) return;

        setValidatingToken(true);
        setError(null);
        setErrorCode(null);

        try {
            const result = await validatePasswordResetToken(token);

            if (result.success) {
                setTokenValid(true);
                setError(null);
                setErrorCode(null);
            } else {
                setTokenValid(false);
                const code = result.errorCode || AuthErrorCode.UNEXPECTED_ERROR;
                setErrorCode(code);
                setError(getErrorMessage(code));
            }
        } catch (err) {
            console.error('Token validation error:', err);
            setTokenValid(false);
            setErrorCode(AuthErrorCode.UNEXPECTED_ERROR);
            setError(getErrorMessage(AuthErrorCode.UNEXPECTED_ERROR));
        } finally {
            setValidatingToken(false);
        }
    };

    if (validatingToken) {
        return <Spinner />;
    }

    return (
        <AuthFormContainer title="Passwort ändern">
            {error && <ErrorAlert message={error} />}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, minWidth: '350px' }}>
                <FormControl fullWidth required>
                    <TextField
                        autoFocus={true}
                        id="password"
                        name="password"
                        placeholder="Neues Passwort"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e)}
                        isDisabled={!tokenValid}
                    />
                </FormControl>

                <FormControl fullWidth required sx={{ mt: 2 }}>
                    <TextField
                        id="confirm-password"
                        name="confirm-password"
                        placeholder="Neues Passwort bestätigen"
                        type="password"
                        value={confirmedPassword}
                        onChange={(e) => setConfirmedPassword(e)}
                        isDisabled={!tokenValid}
                    />
                </FormControl>

                <Button
                    fullWidth={true}
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="mt-4"
                    loading={loading}
                    isDisabled={!tokenValid}
                >
                    Passwort ändern
                </Button>
            </Box>
        </AuthFormContainer>
    );
};

export default ResetPassword;